import { useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import {
    faCircleCheck,
    faEnvelopesBulk,
    faHeart,
    faTruckFast,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateEngageMessageById } from 'api/engage';
import {
    LETTER_START_COST,
    PLUS_LETTER_START_COST,
} from 'assets/js/pages/engage/letters/constants';
import { useLetterActions } from 'assets/js/pages/engage/letters/useLetterActions';
import * as Dialog from 'components/dialog';
import { StepperContext } from 'components/stepper';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { GivebutterAxiosError } from 'types/error';

interface LetterEasySendCTADialogProps extends Dialog.DialogProps {}

const LetterEasySendCTADialog = (props: LetterEasySendCTADialogProps) => {
    const toast = useToast();
    const { children, ...rest } = props;
    const currencySymbol = useCurrencySymbol();
    const { goForward } = useContext(StepperContext);
    const { accountId, hasGivebutterPlusAccess } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const params = useParams<{ letter_id: string }>();
    const messageId = parseInt(params.letter_id);
    const { invalidate, createNewLetterDraftMutation } = useLetterActions();
    const { mutate: createLetterMutation, isPending } = createNewLetterDraftMutation;
    const history = useHistory();

    const { mutate: updateLetterToEasySend } = useMutation({
        mutationFn: () =>
            updateEngageMessageById(accountId, messageId, {
                settings: { send_to_print_and_mail_service: true },
            }),
        onSuccess: async () => {
            await invalidate({ type: 'all' });
            history.push(`/accounts/${accountId}/engage/letters/draft/${messageId}/edit/envelope`);
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;

            toast.error(
                data?.message ??
                    'Error trying to upgrade to an automated mailing, please contact our support.',
            );
        },
    });

    const createOrUpdateManualLetter = () => {
        if (messageId) {
            forceRender();
            goForward();
        } else {
            createLetterMutation(false);
        }
    };

    const createOrUpdateEasySendLetter = () => {
        if (messageId) {
            updateLetterToEasySend();
        } else {
            createLetterMutation(true);
        }
    };

    return (
        <Dialog.Root {...rest} key={key}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="max-w-[68rem] bg-[url('/images/illustrations/easy_send_cta_bg.svg')] bg-right-bottom bg-no-repeat px-16 py-10">
                    <div className="relative flex flex-col gap-12">
                        <div className="flex flex-col gap-2 text-center">
                            <div className="m-auto max-w-[42rem] text-4xl font-medium">
                                Launch direct mail campaigns from
                                <br />
                                start to send 📫
                            </div>
                            <div className="text-xl text-gray-600">
                                Skip stamping and stuffing envelopes—we'll handle the hassle for
                                you!
                            </div>
                        </div>

                        <div className="flex flex-col gap-10 md:flex-row">
                            <div className="flex w-full flex-col gap-8 md:w-1/2">
                                <div className="flex gap-8">
                                    <div>
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-100 shadow-sm">
                                            <FontAwesomeIcon
                                                icon={faEnvelopesBulk}
                                                className="text-violet-500"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-medium">Personalized letters</div>
                                        <div className="text-gray-600">
                                            Engage your donors with personal touches like first
                                            names and adding a personalized note to individual
                                            letters.
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-8">
                                    <div>
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-100 shadow-sm">
                                            <FontAwesomeIcon
                                                icon={faHeart}
                                                className="text-violet-500"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-medium">Prepared with care</div>
                                        <div className="text-gray-600">
                                            Your letters will be printed out, labeled, and securely
                                            sealed in envelopes before they're sent off.
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-8">
                                    <div>
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-100 shadow-sm">
                                            <FontAwesomeIcon
                                                icon={faTruckFast}
                                                className="text-violet-500"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-medium">
                                            Scheduled, sent, and tracked
                                        </div>
                                        <div className="text-gray-600">
                                            You can schedule or send letters immediately. Then,
                                            track their journey and donation conversions.
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <Button
                                        variant="transparent"
                                        disabled={isPending}
                                        onClick={() => createOrUpdateManualLetter()}
                                        size="lg"
                                    >
                                        I'll print &amp; send them myself
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </Button>
                                </div>
                            </div>
                            <div className="w-full rounded-xl border border-gray-200 bg-white shadow-2xl md:w-1/2">
                                <div className="flex flex-col items-center justify-center gap-2 px-8 py-4 text-lg font-medium">
                                    <div className="text-gray-600">
                                        {currencySymbol}
                                        {hasGivebutterPlusAccess
                                            ? PLUS_LETTER_START_COST
                                            : LETTER_START_COST}
                                        /letter
                                    </div>
                                    <div className="text-3xl font-medium">Automated mailing</div>
                                </div>
                                <Separator />
                                <div className="flex flex-col gap-10 p-8">
                                    <ul className="flex flex-col gap-4 text-lg">
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Printing
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Postage
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Embeddable QR codes
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Individual letter customization
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Donation conversion analytics
                                        </li>
                                    </ul>
                                    <Button
                                        variant="solid"
                                        size="lg"
                                        disabled={isPending}
                                        onClick={() => createOrUpdateEasySendLetter()}
                                        className="w-full font-semibold"
                                    >
                                        Get started
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <img
                            src={'/images/illustrations/easy_send_cta_confetti.svg'}
                            className="z-belowZero absolute -left-8 -top-8"
                            alt={'Easy send confetti top left'}
                        />
                        <img
                            src={'/images/illustrations/easy_send_cta_confetti.svg'}
                            className="z-belowZero absolute right-0 top-12 rotate-180"
                            alt={'Easy send confetti top right'}
                        />
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const LetterEasySendCTADialogTrigger = Dialog.Trigger;
const Root = LetterEasySendCTADialog;

const Trigger = LetterEasySendCTADialogTrigger;

export {
    LetterEasySendCTADialog,
    LetterEasySendCTADialogTrigger,
    //
    Root,
    Trigger,
};
